import moment from 'moment-timezone';
import { PreMidnightTime } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesDetails/utils';
import { IPeriodOption, PeriodType } from 'src/store/modules/adManager/adScheduledActionGroups/selectors';

// Constants
export const NEVER_END_DATE = '02/02/9999';
export const MOMENT_NEVER_END_DATE = moment(NEVER_END_DATE);

// Types
export interface GeneratedHourProps {
  id: string;
  label: string;
}

export enum TimeZone {
  PacificTime = 'America/Los_Angeles',
  EasternTime = 'America/New_York',
  CentralTime = 'America/Chicago',
  MountainTime = 'America/Denver',
  WesternEuropeanTime = 'Europe/London',
  CentralEuropeanTime = 'Europe/Berlin',
  EasternEuropeanTime = 'Europe/Kiev',
  ArabiaStandardTime = 'Asia/Riyadh',
  IndiaStandardTime = 'Asia/Kolkata',
  ChinaStandardTime = 'Asia/Shanghai',
  JapanStandardTime = 'Asia/Tokyo',
  KoreaStandardTime = 'Asia/Seoul',
  IndochinaTime = 'Asia/Bangkok',
  SingaporeStandardTime = 'Asia/Singapore',
  PhilippineTime = 'Asia/Manila'
}

// Display names mapping for user interface
export const timeZoneDisplayNames: Record<TimeZone, string> = {
  [TimeZone.PacificTime]: 'Pacific Time',
  [TimeZone.EasternTime]: 'Eastern Time',
  [TimeZone.CentralTime]: 'Central Time',
  [TimeZone.MountainTime]: 'Mountain Time',
  [TimeZone.WesternEuropeanTime]: 'UTC',
  [TimeZone.CentralEuropeanTime]: 'Central European Time',
  [TimeZone.EasternEuropeanTime]: 'Eastern European Time',
  [TimeZone.ArabiaStandardTime]: 'Arabia Standard Time',
  [TimeZone.IndiaStandardTime]: 'India Standard Time',
  [TimeZone.ChinaStandardTime]: 'China Standard Time',
  [TimeZone.JapanStandardTime]: 'Japan Standard Time',
  [TimeZone.KoreaStandardTime]: 'Korea Standard Time',
  [TimeZone.IndochinaTime]: 'Indochina Time',
  [TimeZone.SingaporeStandardTime]: 'Singapore Standard Time',
  [TimeZone.PhilippineTime]: 'Philippine Time'
};

/**
 * Generates ID and label for a specific hour
 */
export const generateDayIdLabel = (hour: number): GeneratedHourProps => {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const hourLabel = hour % 12 || 12;
  if (hour === 24) {
    return { id: String(PreMidnightTime), label: '11:59 PM' };
  }
  return { id: hour.toString(), label: `${hourLabel}:00 ${suffix}` };
};

/**
 * Generates an array of hour options for a day
 */
export const generateDayHours = (): GeneratedHourProps[] => {
  const hours = [] as GeneratedHourProps[];
  for (let i = 0; i < 24; i++) {
    hours.push(generateDayIdLabel(i));
  }
  hours.push({ id: String(PreMidnightTime), label: '11:59 PM' });
  return hours;
};

/**
 * Gets the current UTC offset for a timezone in hours
 */
export const getTimezoneOffset = (timezone: TimeZone): number => {
  const offsetInHours = moment().tz(timezone).utcOffset() / 60;
  return offsetInHours;
};

/**
 * Converts time in a specific timezone to a period option
 */
export const timeZoneToDateTime = (timezone: TimeZone, startTime: number, endTime: number): IPeriodOption => {
  const now = moment().tz(timezone);
  const localDayBeginning = now.clone().add(1, 'days').startOf('day');

  const startDateTime = localDayBeginning.clone().add(startTime, 'hours').utc();
  const endDateTime = localDayBeginning.clone().add(endTime, 'hours').utc();
  const timeZoneOffset = getTimezoneOffset(timezone);
  const startOffSet = Math.floor((startTime - timeZoneOffset) / 24);
  const endOffSet = Math.floor((endTime - timeZoneOffset) / 24);

  return {
    name: PeriodType.HOURS_IN_DAY,
    displayName: timeZoneDisplayNames[timezone],
    startDate: localDayBeginning.format('YYYY/MM/DD'),
    endDate: MOMENT_NEVER_END_DATE,
    startTime: startDateTime.format('HH:mm'),
    endTime: endDateTime.format('HH:mm'),
    startOffSet,
    endOffSet
  };
};
